import React, { useEffect, useMemo, useState } from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import { hot } from 'react-hot-loader/root'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import ThemeSwitch from '@ta-interaktiv/react-theme-switch'
import { HearkenEmbed } from 'components/hearkenEmbed'
import Tabletop from 'tabletop'
import { HearkenStatus } from 'types/hearkenStatus'
import {
  GoogleSheetResponse,
  HearkenConfiguration
} from 'types/googleSheetResponse'
// import logo from 'images/logo-outlined.svg'
import logo from 'images/corona-virus.svg'
import Intro from 'text/intro.md'
import styles from './styles.module.scss'
import { Steps } from 'components/steps'
import { CollectionCategory } from 'components/collectionCategory'
import config from 'config'
import { VoteChart } from 'components/voteChart'

// TODO Set the correct article ID here
const ARTICLE_ID = config.articleId

interface Category {
  Spaltenname: string
  Header: string
  Subheader: string
}

function AppComponent() {
  const [hearkenStatus, setHearkenStatus] = useState<HearkenStatus>(
    HearkenStatus.PUBLISHING
  )
  const [allStatus, setAllStatus] = useState<HearkenConfiguration>({
    hearkenStatus: HearkenStatus.PUBLISHING
  })
  const [categories, setCategories] = useState<Category[]>([])
  const [articleIDsByCategory, setArticleIDs] = useState(
    new Map<string, string[]>()
  )

  useEffect(() => {
    Tabletop.init({
      key: config.googleSheetKey,
      simpleSheet: false,
      callback: (data: GoogleSheetResponse) => {
        // Set Hearken Status
        setAllStatus(data.Status.elements[0])
        setHearkenStatus(data.Status.elements[0].hearkenStatus as HearkenStatus)

        // Gather all categories
        const categories = data.Kategorien.elements
        setCategories(categories)
        const articleRows = data.Artikel.elements
        const articleIDsPerCategory = new Map<string, string[]>()

        categories.forEach(category => {
          articleIDsPerCategory.set(
            category.Spaltenname,
            articleRows.reduce<string[]>((previousValue, currentValue) => {
              const id = currentValue[category.Spaltenname]
              if (id && /\d{8}/.test(id)) {
                previousValue.push(id)
              }
              return previousValue
            }, [])
          )
        })

        setArticleIDs(articleIDsPerCategory)
      }
    })
  }, [])

  const areAnyArticlesAvailable = useMemo(
    () =>
      Array.from(articleIDsByCategory.values()).some(value => value.length > 0),
    [articleIDsByCategory]
  )

  return (
    <div className={'index ' + styles.index}>
      <Masthead articleId={ARTICLE_ID} />
      <ThemeSwitch />

      <header className={'ui vertical very relaxed segment ' + styles.cover}>
        <div className='ui container'>
          <h1 className='ui image centered header'>
            <img src={logo} alt='Was Zürich wissen will' className='ui image' />
            <div className='content' style={{ color: 'white' }}>
              Immer daheim, das gibt Probleme – für welche sollen wir Lösungen
              suchen?
              <div className='sub header' />
            </div>
          </h1>
        </div>
      </header>

      <div className='ui vertical segment'>
        <div className='ui text container'>
          <Intro />
        </div>
      </div>

      <div className='ui vertical segment'>
        <div className='ui text container'>
          <Steps {...allStatus} />
        </div>
      </div>

      {navigator.userAgent !== 'ReactSnap' && (
        <div className='ui vertical segment'>
          <div className='ui text container'>
            <HearkenEmbed hearkenStatus={hearkenStatus} />
            <VoteChart {...allStatus} />
          </div>
        </div>
      )}

      {categories.length > 0 && areAnyArticlesAvailable && (
        <div
          id='articles'
          className={
            'ui vertical inverted segment ' + styles.categoryBackground
          }
        >
          <div className='ui container'>
            <h2 className={styles.articlesHeader}>
              Folgende Beiträge entstanden dank Ihrer Fragen:
            </h2>
          </div>
        </div>
      )}

      {categories.map(category => (
        <CollectionCategory
          key={category.Spaltenname}
          {...category}
          articleIds={articleIDsByCategory.get(category.Spaltenname) || []}
        />
      ))}

      {/* TODO Add your credits in this component */}
      <Footer
        mailTo='bern@derbund.ch'
        articleId={ARTICLE_ID}
        questions={[
          'Wie gefällt Ihnen, dass Sie die Themen mitbestimmen können?'
        ]}
        credits={[
          ['Projekt', 'Christoph Aebischer, Christian Zellweger'],
          ['Projektleitung Tamedia', 'Dinja Plattner'],
          ['Programmierung', 'Christian Zellweger'],
          ['Leitung Redaktion Bern', 'Marcello Odermatt']
        ]}
      />
    </div>
  )
}

export const App = hot(AppComponent)
